import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import {
  Description as DocumentIcon,
  CheckCircle as ValidIcon,
  Schedule as ExpiringIcon,
  Error as ExpiredIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API } from '../../API/api';
import { useAuth } from '../../Context/AuthContext';

const DocumentStatistics = () => {
  const { authState } = useAuth();
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  useEffect(() => {
    fetchStatistics();
  }, [timeRange]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/documents/statistics`, {
        headers: { Authorization: `Bearer ${authState.token}` },
      });
      setStatistics(response.data.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusData = () => {
    return [
      { name: 'Valides', value: statistics.valid || 0, color: '#4CAF50' },
      { name: 'Expirent bientôt', value: statistics.expiring_soon || 0, color: '#FF9800' },
      { name: 'Expirés', value: statistics.expired || 0, color: '#F44336' },
    ];
  };

  const getExpiringByMonthData = () => {
    if (!statistics.expiring_by_month) return [];
    
    return statistics.expiring_by_month.map(item => ({
      month: getMonthName(item.month),
      count: item.count,
    }));
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
      'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'
    ];
    return months[monthNumber - 1] || `Mois ${monthNumber}`;
  };

  const getDocumentTypeData = () => {
    if (!statistics.document_type_stats) return [];
    return statistics.document_type_stats;
  };

  const getEntityTypeData = () => {
    if (!statistics.entity_type_stats) return [];
    return statistics.entity_type_stats;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 , fontFamily: '"Montserrat", sans-serif' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight={"bold"} gutterBottom>
          Statistiques des Documents
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Analysez les tendances et la répartition de vos documents
        </Typography>
      </Box>

      {/* Time Range Selector */}
      <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <FormControl size="small">
                <InputLabel>Période</InputLabel>
                <Select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  label="Période"
                >
                  <MenuItem value="7">7 derniers jours</MenuItem>
                  <MenuItem value="30">30 derniers jours</MenuItem>
                  <MenuItem value="90">90 derniers jours</MenuItem>
                  <MenuItem value="365">1 an</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <DocumentIcon />
                </Avatar>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Documents
                  </Typography>
                  <Typography variant="h4">
                    {statistics.total || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <ValidIcon />
                </Avatar>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Valides
                  </Typography>
                  <Typography variant="h4">
                    {statistics.valid || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }} >
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <ExpiringIcon />
                </Avatar>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Expirent bientôt
                  </Typography>
                  <Typography variant="h4">
                    {statistics.expiring_soon || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }} >
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                  <ExpiredIcon />
                </Avatar>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Expirés
                  </Typography>
                  <Typography variant="h4">
                    {statistics.expired || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Status Distribution Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }} >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Répartition par Statut
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={getStatusData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getStatusData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Expiring by Month Bar Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Documents Expirant par Mois
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getExpiringByMonthData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Document Types Bar Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }} >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Répartition par Type de Document
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getDocumentTypeData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Entity Types Bar Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Répartition par Type d'Entité
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getEntityTypeData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#ffc658" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Statistics Table */}
      <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Statistiques Détaillées
          </Typography>
          <TableContainer component={Paper} sx={{ 
               
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '10px' },               
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }} >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Métrique</TableCell>
                  <TableCell align="right">Valeur</TableCell>
                  <TableCell align="right">Pourcentage</TableCell>
                  <TableCell>Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Documents Valides</TableCell>
                  <TableCell align="right">{statistics.valid || 0}</TableCell>
                  <TableCell align="right">
                    {statistics.total ? ((statistics.valid / statistics.total) * 100).toFixed(1) : 0}%
                  </TableCell>
                  <TableCell>
                    <Chip label="Bon" color="success" size="small" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Documents Expirant Bientôt</TableCell>
                  <TableCell align="right">{statistics.expiring_soon || 0}</TableCell>
                  <TableCell align="right">
                    {statistics.total ? ((statistics.expiring_soon / statistics.total) * 100).toFixed(1) : 0}%
                  </TableCell>
                  <TableCell>
                    <Chip label="Attention" color="warning" size="small" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Documents Expirés</TableCell>
                  <TableCell align="right">{statistics.expired || 0}</TableCell>
                  <TableCell align="right">
                    {statistics.total ? ((statistics.expired / statistics.total) * 100).toFixed(1) : 0}%
                  </TableCell>
                  <TableCell>
                    <Chip label="Critique" color="error" size="small" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Total Documents</strong></TableCell>
                  <TableCell align="right"><strong>{statistics.total || 0}</strong></TableCell>
                  <TableCell align="right"><strong>100%</strong></TableCell>
                  <TableCell>
                    <Chip label="Total" color="primary" size="small" />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DocumentStatistics;
